/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { Container } from 'pixi.js';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { isConnectedBlocker } from 'game/boardMVC/utils/isConnectedBlocker';
import { TRAY_SETTINGS } from '../game/scene/GameSceneConstants';
import { BoardModel } from '../game/boardMVC/models/board/BoardModel';
import { EditorTrayModel } from './tray/EditorTrayModel';
import type { LevelData } from '../game/level/LevelData';
import { BoardViewScheduler } from '../game/boardMVC/models/board/ViewScheduler';
import { EditorOverlay } from './HTML/EditorOverlay';
import { EMPTY_LEVEL } from '../game/level/EmptyLevel';
import { DEFAULT_BLOCKER_STATES, DEFAULT_LEVEL_DATA } from './constants';
import { EditorHistoryManager } from './EditorHistoryManager';
import { EditMode } from './types';
import { EditorGameAreaView } from './EditorGameAreaView';
import type { TileStackModel } from '../game/boardMVC/models/board/TileStackModel';
import { TileStackView } from '../game/boardMVC/views/board/TileStackView';
import { TileColor } from '../game/boardMVC/TileColor';
import { TileModel } from '../game/boardMVC/models/board/TileModel';
import type { App } from '../app/App';
import { TileView } from '../game/boardMVC/views/board/TileView';
import type { EditorTrayStackView } from './tray/EditorTrayStackView';
import { EMPTY_BACKGROUND_TILE_OPACITY } from './board/EditorBoardView';
import { MS_IN_HOUR } from '../utils/time-utils';
import type { RemixLevelConfig, RemixLevelParameters } from './level/types';
import { generateRemixedLevel } from './level/RemixLevel';
import { createActionDialog } from '../libs/HTML/HTMLElementCreator';
import type { BlockerType } from '../game/boardMVC/types';
import { removeConnections } from './utils/EditorBlockerConnectionUtils';
import { EditorWinRateCalculator } from './EditorWinRateCalculator';
import { getLevelDataHash } from '../../levels';

export interface EditorControllerConfig {
  app: App;
  levelData: LevelData | undefined;
  dragContainer: Container;
  parent: Container;
}

let currentLevelData: LevelData = cloneDeep(DEFAULT_LEVEL_DATA);

export class EditorController {
  private readonly overlay: EditorOverlay = this.initHTMLOverlay();

  private editMode: EditMode = EditMode.Default;

  private boardModel?: BoardModel;

  private gameAreaView?: EditorGameAreaView;

  private editorWinRateCalculator?: EditorWinRateCalculator;

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    // Ignore when typing in inputs or textareas or contenteditable elements
    const target = e.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.getAttribute('contenteditable') === 'true')
    )
      return;

    if (this.editMode !== EditMode.EditTiles) return;

    const { key } = e;
    if (!/^\d$/.test(key)) return;

    const amount = Number(key);
    if (amount <= 0) return;

    const areaView = this.gameAreaView!;
    if (!this.boardModel) return;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const hoveredPos = areaView.getHoveredGridPos() as { x: number; y: number } | null;
    if (!hoveredPos) return;
    const { x, y } = hoveredPos as { x: number; y: number };

    const stackModel = this.boardModel.getStack(x, y);
    if (!stackModel) return;

    const stackView = areaView.boardView.getStackView(stackModel.id);
    if (!stackView) return;

    this.editBoardTilesBatch(stackModel, stackView, amount);
  };

  constructor(private readonly config: EditorControllerConfig) {
    const levelData = this.config.levelData ?? currentLevelData;
    this.overlay.updateLevelData(levelData);
    this.gameAreaView = this.initGameAreaView(levelData);
    window.addEventListener('keydown', this.onKeyDown);
  }

  private initHTMLOverlay(): EditorOverlay {
    return new EditorOverlay({
      headerConfig: {
        onNewBoard: (cols: number, rows: number): void => {
          const newLevelData = cloneDeep(EMPTY_LEVEL);
          newLevelData.board.cols = cols;
          newLevelData.board.rows = rows;
          this.restoreState(newLevelData);
        },
        onUndo: (): void => {
          const undo = EditorHistoryManager.instance.removeLastState();
          if (undo) {
            this.restoreState(undo.levelData, false);
            this.overlay.updateLevelData(undo.levelData);
            this.switchEditMode(undo.editMode);
          }
        },
        onModeChange: (value): void => {
          this.editMode = value;
          this.gameAreaView?.refreshEditMode(this.editMode);
        },
      },
      onStateChanged: (deltaState?: Partial<LevelData>): void => {
        this.recordHistoryState();
        this.updateLevelData({
          ...this.exportLevelData(),
          ...deltaState,
        });
      },
      getGameAreaView: () => this.gameAreaView,
      refreshBlockerView: (stackModel: TileStackModel): void => {
        this.gameAreaView?.refreshBlockerView(stackModel);
      },
      runLevelRemix: (remixParameters: RemixLevelParameters) => this.remixLevelData(remixParameters),
    });
  }

  private initGameAreaView(levelData: LevelData): EditorGameAreaView {
    this.gameAreaView?.destroy();

    this.boardModel = new BoardModel({
      state: levelData.board,
      scheduler: new BoardViewScheduler(),
    });

    const trayModel = new EditorTrayModel(levelData.tray);

    const view = new EditorGameAreaView({
      app: this.config.app,
      boardModel: this.boardModel,
      trayModel,
      trayViewSettings: { ...TRAY_SETTINGS },
    });
    view.props({
      parent: this.config.parent,
    });

    this.editorWinRateCalculator = new EditorWinRateCalculator(view, levelData);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.editorWinRateCalculator.onWinRateUpdated.connect((winRateMsg: string) => {
      view.displayWinRate(winRateMsg);
    });

    view.updateHash(getLevelDataHash(levelData));

    view.refreshEditMode(this.editMode);

    view.onGridPointerDown.connect((x: number, y: number) => {
      const { editMode } = this;
      const { boardView } = view;
      const stackModel = this.boardModel?.getStack(x, y);
      const stackView = stackModel?.id ? boardView.getStackView(stackModel.id) : null;
      const stackExists = !!(stackModel && stackView);

      if (editMode === EditMode.EditTiles && stackExists) {
        this.editBoardTiles(stackModel, stackView);
      } else if (editMode === EditMode.PaintBlockers && stackExists) {
        this.editBoardBlockers(stackModel);
      } else if (editMode === EditMode.PaintUnderTileBlockers && stackExists) {
        this.editBoardUtBlockers(stackModel);
      } else if (editMode === EditMode.ToggleStacks) {
        this.toggleBoardStacks(x, y);
      } else if (editMode === EditMode.EditBlockers) {
        this.switchEditMode(EditMode.PaintBlockers);
      } else if (editMode === EditMode.EditBlockerConnections && stackExists) {
        this.overlay.blockerConnectionsPanel.edit(stackModel);
      }
    });

    view.trayView.onStackPointerDown.connect((stackView: EditorTrayStackView) => {
      const stackModel = stackView.getModel();
      this.editBoardTiles(stackModel, stackView);
    });

    return view;
  }

  private editBoardBlockers(stackModel: TileStackModel): void {
    const { boardView } = this.gameAreaView!;
    const blockerType = this.overlay.blockerSelector.selectedValue as BlockerType;
    const stackView = boardView.getStackView(stackModel.id);
    const existingBlockerView = stackModel.blocker?.id ? boardView.getBlockerView(stackModel.blocker.id) : null;

    if (!blockerType && existingBlockerView) {
      if (stackModel.blocker && isConnectedBlocker(stackModel.blocker) && this.gameAreaView) {
        removeConnections(boardView.getModel(), this.gameAreaView, stackModel);
      }

      // Delete existing blocker
      stackModel.setBlocker(null);
      existingBlockerView.destroy();
      stackView?.reset();
      this.recordHistoryState();
      this.updateLevelData();
    } else if (existingBlockerView) {
      // Open blocker for editing
      this.switchEditMode(EditMode.EditBlockers);
      this.overlay.blockersPanel.edit(stackModel);
    } else if (blockerType) {
      // Add new blocker
      const blockerState = DEFAULT_BLOCKER_STATES[blockerType];

      boardView.getModel().initBlockers([
        {
          ...blockerState,
          x: stackModel.x,
          y: stackModel.y,
        },
      ]);
      this.gameAreaView?.refreshBlockerView(stackModel);

      this.recordHistoryState();
      this.updateLevelData();
    }
  }

  private editBoardUtBlockers(stackModel: TileStackModel): void {
    const { boardView } = this.gameAreaView!;
    const blockerType = this.overlay.utBlockerSelector.selectedValue as BlockerType;
    const stackView = boardView.getStackView(stackModel.id);
    const existingBlockerView = stackModel.underTileBlocker?.id
      ? boardView.getBlockerView(stackModel.underTileBlocker.id)
      : null;

    if (!blockerType && existingBlockerView) {
      // Delete existing blocker
      stackModel.setUnderTileBlocker(null);
      existingBlockerView.destroy();
      stackView?.reset();

      this.recordHistoryState();
      this.updateLevelData();
    } else if (existingBlockerView) {
      // Open blocker for editing
      this.switchEditMode(EditMode.EditUnderTileBlockers);
      this.overlay.blockersPanel.edit(stackModel);
    } else if (blockerType) {
      // Add new blocker
      const blockerState = DEFAULT_BLOCKER_STATES[blockerType];

      boardView.getModel().initBlockers([
        {
          ...blockerState,
          x: stackModel.x,
          y: stackModel.y,
        },
      ]);
      this.gameAreaView?.refreshUnderTileBlockerView(stackModel);

      this.recordHistoryState();
      this.updateLevelData();
    }
  }

  private editBoardTiles(stackModel: TileStackModel, stackView: TileStackView | EditorTrayStackView): void {
    const color = this.overlay.colorSelector.selectedValue as TileColor;

    if (color !== TileColor.None) {
      const tileModel = new TileModel(color);
      stackModel.addTiles(tileModel);
      stackView.addTileView(new TileView(tileModel));

      this.recordHistoryState();
      this.updateLevelData();
    } else {
      const tileId = stackModel.getTopmostTile()?.id;
      if (!tileId) return;

      const tileView = stackView.getTileView(tileId);
      if (!tileView) return;

      stackView.removeTile(tileView);
      stackModel.removeTilesAmount(1);

      this.recordHistoryState();
      this.updateLevelData();
    }

    // repositions blocker if one exists.
    if (stackModel.blocker && stackView instanceof TileStackView) {
      this.gameAreaView?.refreshBlockerView(stackModel);
    }
  }

  private editBoardTilesBatch(stackModel: TileStackModel, stackView: TileStackView, count: number): void {
    const color = this.overlay.colorSelector.selectedValue as TileColor;
    if (color === TileColor.None || count <= 0) return;

    const newTiles: TileModel[] = [];
    for (let i = 0; i < count; i += 1) newTiles.push(new TileModel(color));
    stackModel.addTiles(newTiles);
    stackView.addTileView(newTiles.map((m) => new TileView(m)));

    this.recordHistoryState();
    this.updateLevelData();

    if (stackModel.blocker && stackView instanceof TileStackView) {
      this.gameAreaView?.refreshBlockerView(stackModel);
    }
  }

  private toggleBoardStacks(x: number, y: number): void {
    const {
      boardView,
      boardView: { background },
    } = this.gameAreaView!;
    const boardModel = boardView.getModel();
    let stackModel = boardModel.getStack(x, y);

    if (!stackModel) {
      stackModel = boardModel.createStack(x, y);
      boardView.createTileStackView(stackModel);
      background.getTile(stackModel.x, stackModel.y)?.props({
        alpha: 1,
      });
    } else {
      boardModel.removeStack(stackModel);
      boardView.getStackView(stackModel.id)?.destroy();
      background.getTile(stackModel.x, stackModel.y)?.props({
        alpha: EMPTY_BACKGROUND_TILE_OPACITY,
      });
    }

    this.recordHistoryState();
    this.updateLevelData();
  }

  private async remixLevelData(remixParameters: RemixLevelParameters): Promise<void> {
    const originalModel = this.gameAreaView?.boardView.getModel();
    if (!originalModel) return;

    const levelData = this.exportLevelData();

    const remixConfig: RemixLevelConfig = {
      ...remixParameters,
      originalLevel: levelData,
      timeoutMs: MS_IN_HOUR,
    };

    const dialog = createActionDialog({
      parentElement: document.body,
      titleText: 'Attempting to Remix',
      zIndex: Number.MAX_SAFE_INTEGER,
      showConfirm: false,
      onCancel: () => {
        remixConfig.cancelled = true;
      },
    });

    const remixedLevelData = await generateRemixedLevel(remixConfig);
    if (remixedLevelData) this.restoreState(remixedLevelData);

    dialog.remove();
  }

  private switchEditMode(mode: EditMode): void {
    this.editMode = mode;
    this.overlay.setEditorModeActive(this.editMode);
    this.gameAreaView?.refreshEditMode(mode);
  }

  public recordHistoryState(): void {
    const history = EditorHistoryManager.instance;

    if (!isEqual(history.getLastState()?.levelData, currentLevelData)) {
      history.addState({
        levelData: cloneDeep(currentLevelData),
        editMode: this.editMode,
      });
    }
  }

  private updateLevelData(levelData?: LevelData): void {
    currentLevelData = cloneDeep(levelData) ?? cloneDeep(this.exportLevelData());
    this.overlay.updateLevelData(currentLevelData);
    this.gameAreaView?.updateHash(getLevelDataHash(currentLevelData));
    this.editorWinRateCalculator?.updateLevel(currentLevelData);
  }

  public restoreState(levelData: LevelData, recordHistoryState = true): void {
    if (recordHistoryState) this.recordHistoryState();

    console.log('restoring state', getLevelDataHash(levelData));

    this.updateLevelData(levelData);

    const { propertiesPanel, blockersPanel } = this.overlay;
    propertiesPanel.update(levelData);
    blockersPanel.stopEditing();

    this.gameAreaView = this.initGameAreaView(levelData);
  }

  public exportLevelData(): LevelData {
    if (!this.gameAreaView) {
      return cloneDeep(DEFAULT_LEVEL_DATA);
    }

    return {
      ...currentLevelData,
      board: this.gameAreaView.boardView.getModel().exportState(),
      tray: this.gameAreaView.trayView.getModel().exportState(),
    };
  }

  public getLevelHash(): string {
    return getLevelDataHash(this.exportLevelData());
  }

  public update(deltaTime: number): void {
    this.gameAreaView?.boardView.getModel().update(deltaTime);
    this.gameAreaView?.update(deltaTime);
  }

  public destroy(): void {
    this.gameAreaView?.destroy();
    this.overlay.destroy();
    window.removeEventListener('keydown', this.onKeyDown);
  }
}
